<template>
    <div class="container-fluid p-4 p-md-5">
        <div class="row g-4 g-lg-5">
            <div class="col-lg-4 d-flex flex-column mt-0">
                <div class="h-100 pe-lg-3">
                    <WidgetHeader
                        v-model="cookieConsentExp"
                        :selected-widget="selectedWidget"
                        :title="headerTitle"
                        :description="headerDescription"
                        :step-config="currentStepConfig"
                        @update-step="handleUpdateStep($event)"
                        @input="cookieConsentExp = $event"
                        @activity-selected="selectedActivityId = $event"
                        @event-selected="selectedEventId = $event"
                        @formule-selected="selectedFormuleCombineeId = $event"
                        @forfait-selected="selectedForfaitId = $event"
                        @serie-selected="selectedSerieId = $event"
                        @voucher-selected="selectedVoucherId = $event"
                        @devis-selected="selectedDevisId = $event"
                        @category-selected="selectedCategoryId = $event"
                    />

                    <div
                        class="widget-options d-flex flex-column gap-4 flex-grow-1 overflow-y-auto ppv2-mb-3"
                        style="max-height: calc(100vh - 280px)"
                    >
                        <div v-if="currentStep === 1" class="d-flex flex-column gap-4">
                            <WidgetCategory
                                :title="$t('widgetIntegration.categories.mostUsed')"
                                :widgets="widgets.filter((w) => w.category === 'most_used')"
                                :selectedWidgetId="selectedWidget?.id"
                                :isHighlighted="true"
                                @select="selectWidget"
                            />
                            <WidgetCategory
                                :title="$t('widgetIntegration.categories.offres')"
                                :widgets="widgets.filter((w) => w.category === 'offres')"
                                :selectedWidgetId="selectedWidget?.id"
                                @select="selectWidget"
                            />
                            <WidgetCategory
                                :title="$t('widgetIntegration.categories.ventes')"
                                :widgets="widgets.filter((w) => w.category === 'ventes')"
                                :selectedWidgetId="selectedWidget?.id"
                                @select="selectWidget"
                            />
                            <WidgetCategory
                                :title="$t('widgetIntegration.categories.restaurant')"
                                :widgets="widgets.filter((w) => w.category === 'restaurant')"
                                :selectedWidgetId="selectedWidget?.id"
                                @select="selectWidget"
                            />
                        </div>
                    </div>
                    <WidgetFooter
                        :isBackButtonVisible="isBackButtonVisible"
                        :isActionButtonVisible="isActionButtonVisible"
                        :loading="submitting"
                        :buttonText="buttonText"
                        @handleBackClick="handleBackClick"
                        @handleActionButtonClick="handleActionButtonClick"
                    />
                </div>
            </div>
            <div class="col-lg-8 d-flex flex-column mt-0">
                <IntegrationPreview
                    :selected-widget="selectedWidget"
                    :selected-item="selectedItem"
                    :iframe-src="iframeSrc"
                    :url-front="url_front"
                    :generatedCodeSnippet="generatedCodeSnippet"
                    :activity-is-valid="activityIsValid"
                    :is-loading-iframe="isLoadingIframe"
                    :cookie-consent-exp="cookieConsentExp"
                    :background-color="backgroundColor"
                    @iframe-load="onIframeLoad"
                    :activities="activities"
                    :events="events"
                    :formulesCombinees="formulesCombinees"
                    @formules-loaded="isLoadingIframe = false"
                    :forfaits="forfaits"
                    @forfaits-loaded="isLoadingIframe = false"
                    @events-loaded="isLoadingIframe = false"
                    @gift-loaded="isLoadingIframe = false"
                    :series="series"
                    @series-loaded="isLoadingIframe = false"
                    :devis="devis"
                    @devis-loaded="isLoadingIframe = false"
                    :categories="categories"
                    :category-prestations="categoryPrestations"
                    @categories-loaded="isLoadingIframe = false"
                />
            </div>
        </div>

        <CodeModal
            v-if="isModalVisible"
            :selectedWidget="selectedWidget"
            :code-snippet="generatedCodeSnippet"
            :widget-id="selectedWidget ? selectedWidget.id : ''"
            @copied="onCodeCopied"
            @close="isModalVisible = false"
            :base_url="url_back"
        />
    </div>
</template>

<script>
import axios from 'axios'
import CodeModal from './CodeModal.vue' // Assurez-vous que le chemin est correct
import WidgetHeader from './WidgetHeader.vue' // Assuming this component is in the same directory
import WidgetFooter from './WidgetFooter.vue'
import WidgetCategory from './WidgetCategory.vue'
import IntegrationPreview from './IntegrationPreview.vue'
export default {
    props: ['url_front', 'url_back', 'lang'],
    components: {
        CodeModal,
        WidgetHeader,
        WidgetFooter,
        WidgetCategory,
        IntegrationPreview,
    },
    data() {
        return {
            selectedWidget: null,
            currentStep: 1,
            cookieConsentExp: false,
            activities: [],
            events: [],
            formulesCombinees: [],
            series: [],
            vouchers: [],
            devis: [],
            forfaits: [],
            categories: [],
            categoryPrestations: [],
            selectedDevisId: null,
            selectedActivityId: null,
            selectedSerieId: null,
            selectedVoucherId: null,
            selectedFormuleCombineeId: null,
            selectedEventId: null,
            selectedForfaitId: null,
            selectedCategoryId: null,
            isLoadingIframe: false,
            isModalVisible: false,
            generatedCodeSnippet: '',
            backgroundColor: '#ffffff',
            activityError: null,
            activityIsValid: true,
            submitting: false,
            isCopied: false,
        }
    },
    computed: {
        widgets() {
            return [
                {
                    id: 'activite',
                    category: 'most_used',
                    title: this.$t('widgetIntegration.widgets.activite.title'),
                    description: this.$t('widgetIntegration.widgets.activite.description'),
                    url_front: `${this.url_front}/discover/reservation`,
                    needsActivitySelection: true,
                    iconName: 'LayoutGrid',
                    stepHeaders: {
                        1: {
                            title: this.$t('widgetIntegration.stepHeaders.integration.title'),
                            description: this.$t('widgetIntegration.stepHeaders.integration.description'),
                        },
                        2: {
                            title: this.$t('widgetIntegration.stepHeaders.activityPage.title'),
                            description: this.$t('widgetIntegration.stepHeaders.activityPage.description'),
                        },
                    },
                    key: 2,
                },
                {
                    id: 'formules',
                    category: 'most_used',
                    title: this.$t('widgetIntegration.widgets.formules.title'),
                    description: this.$t('widgetIntegration.widgets.formules.description'),
                    url_front: `${this.url_front}/packages`,
                    needsActivitySelection: false,
                    iconName: 'Layers',
                    key: 3,
                },
                {
                    id: 'bon-cadeau',
                    category: 'most_used',
                    title: this.$t('widgetIntegration.widgets.bonCadeau.title'),
                    description: this.$t('widgetIntegration.widgets.bonCadeau.description'),
                    url_front: `${this.url_front}/gift`,
                    needsActivitySelection: false,
                    iconName: 'Gift',
                    key: 4,
                },
                {
                    id: 'evenements',
                    category: 'offres',
                    title: this.$t('widgetIntegration.widgets.evenements.title'),
                    description: this.$t('widgetIntegration.widgets.evenements.description'),
                    url_front: `${this.url_front}/discover/events`,
                    needsActivitySelection: true,
                    iconName: 'Calendar',
                    key: 5,
                    stepHeaders: {
                        1: {
                            title: this.$t('widgetIntegration.stepHeaders.integration.title'),
                            description: this.$t('widgetIntegration.stepHeaders.integration.description'),
                        },
                        2: {
                            title: this.$t('widgetIntegration.stepHeaders.activitySelection.title'),
                            description: this.$t('widgetIntegration.stepHeaders.activitySelection.description'),
                        },
                    },
                },
                {
                    id: 'forfaits',
                    category: 'offres',
                    title: this.$t('widgetIntegration.widgets.forfaits.title'),
                    description: this.$t('widgetIntegration.widgets.forfaits.description'),
                    url_front: `${this.url_front}/discover/offer`,
                    needsActivitySelection: false,
                    iconName: 'CreditCard',
                    key: 6,
                },
                {
                    id: 'abonnements',
                    category: 'offres',
                    title: this.$t('widgetIntegration.widgets.abonnements.title'),
                    description: this.$t('widgetIntegration.widgets.abonnements.description'),
                    url_front: `${this.url_front}/discover/serie`,
                    needsActivitySelection: false,
                    iconName: 'RefreshCw',
                    key: 7,
                },
                {
                    id: 'billets-non-dates',
                    category: 'offres',
                    title: this.$t('widgetIntegration.widgets.billetsNonDates.title'),
                    description: this.$t('widgetIntegration.widgets.billetsNonDates.description'),
                    url_front: `${this.url_front}/discover/reservation/undated-ticket`,
                    needsActivitySelection: false,
                    iconName: 'Ticket',
                    key: 13,
                },
                {
                    id: 'categorie',
                    category: 'offres',
                    title: this.$t('widgetIntegration.widgets.categorie.title'),
                    description: this.$t('widgetIntegration.widgets.categorie.description'),
                    url_front: `${this.url_front}/discover/tag`,
                    needsActivitySelection: true,
                    iconName: 'Tag',
                    key: 8,
                },
                {
                    id: 'panier',
                    category: 'ventes',
                    title: this.$t('widgetIntegration.widgets.panier.title'),
                    description: this.$t('widgetIntegration.widgets.panier.description'),
                    url_front: `${this.url_front}/cart`,
                    needsActivitySelection: false,
                    iconName: 'ShoppingCart',
                    key: 9,
                },
                {
                    id: 'profile',
                    category: 'ventes',
                    title: this.$t('widgetIntegration.widgets.profile.title'),
                    description: this.$t('widgetIntegration.widgets.profile.description'),
                    url_front: `${this.url_front}/profile`,
                    needsActivitySelection: false,
                    iconName: 'User',
                    key: 12,
                },
                {
                    id: 'devis',
                    category: 'ventes',
                    title: this.$t('widgetIntegration.widgets.devis.title'),
                    description: this.$t('widgetIntegration.widgets.devis.description'),
                    url_front: `${this.url_front}`,
                    needsActivitySelection: false,
                    iconName: 'FileText',
                    key: 10,
                },
                {
                    id: 'restaurant',
                    category: 'restaurant',
                    title: this.$t('widgetIntegration.widgets.restaurant.title'),
                    description: this.$t('widgetIntegration.widgets.restaurant.description'),
                    url_front: `${this.url_front}/discover/food`,
                    needsActivitySelection: false,
                    iconName: 'Utensils',
                    stepHeaders: {
                        1: {
                            title: this.$t('widgetIntegration.stepHeaders.integration.title'),
                            description: this.$t('widgetIntegration.stepHeaders.integration.description'),
                        },
                        2: {
                            title: this.$t('widgetIntegration.stepHeaders.activitySelection.title'),
                            description: this.$t('widgetIntegration.stepHeaders.activitySelection.description'),
                        },
                    },
                    key: 11,
                },
            ]
        },
        stepConfigs() {
            const self = this
            return [
                {
                    step: 2,
                    type: 'entity-with-toggle',
                    items: self.activities,
                    editPath: '/back-office/v3/experience/edit/{id}/experience',
                    createPath: '/back-office/v3/experience/create',
                    editLabel: this.$t('widgetIntegration.stepConfigs.modifyExperience'),
                    createLabel: this.$t('widgetIntegration.stepConfigs.createExperience'),
                    event: 'activity-selected',
                },
                {
                    step: 5,
                    type: 'entity-with-toggle',
                    items: self.events,
                    showActions: false,
                    event: 'event-selected',
                },
                {
                    step: 3,
                    type: 'entity-with-toggle',
                    items: self.formulesCombinees,
                    editPath: '/back-office/v3/package/edit/{id}',
                    createPath: '/back-office/v3/package/create',
                    editLabel: this.$t('widgetIntegration.stepConfigs.modifyFormule'),
                    createLabel: this.$t('widgetIntegration.stepConfigs.createFormule'),
                    event: 'formule-selected',
                },
                {
                    step: 7,
                    type: 'entity-with-toggle',
                    items: self.series,
                    editPath: '/back-office/v3/serie/create/{id}',
                    createPath: '/back-office/v3/serie/create',
                    editLabel: this.$t('widgetIntegration.stepConfigs.modifyAbonnement'),
                    createLabel: this.$t('widgetIntegration.stepConfigs.createAbonnement'),
                    event: 'serie-selected',
                },
                {
                    step: 13,
                    type: 'info',
                    message: this.$t('widgetIntegration.stepConfigs.noConfigNeeded'),
                },
                {
                    step: 6,
                    type: 'entity-with-toggle',
                    items: self.forfaits,
                    editPath: '/back-office/v3/treatment/edit/{id}',
                    createPath: '/back-office/v3/treatment/create',
                    editLabel: this.$t('widgetIntegration.stepConfigs.modifyForfait'),
                    createLabel: this.$t('widgetIntegration.stepConfigs.createForfait'),
                    event: 'forfait-selected',
                },
                {
                    step: 10,
                    type: 'entity-with-toggle',
                    items: self.devis,
                    editPath: '/back-office/v3/website-settings',
                    createPath: '/back-office/v3/website-settings',
                    editLabel: this.$t('widgetIntegration.stepConfigs.modifyDevis'),
                    createLabel: this.$t('widgetIntegration.stepConfigs.createDevis'),
                    event: 'devis-selected',
                },
                {
                    step: 8,
                    type: 'categories',
                    items: self.categories,
                    showActions: false,
                    selectLabel: this.$t('widgetIntegration.stepConfigs.selectCategory'),
                    event: 'category-selected',
                },
                {
                    step: 4,
                    type: 'info',
                    message: this.$t('widgetIntegration.stepConfigs.noConfigNeeded'),
                },
                {
                    step: 9,
                    type: 'info',
                    message: this.$t('widgetIntegration.stepConfigs.noConfigNeeded'),
                },
                {
                    step: 11,
                    type: 'info',
                    message: this.$t('widgetIntegration.stepConfigs.noConfigNeeded'),
                },
                {
                    step: 12,
                    type: 'info',
                    message: this.$t('widgetIntegration.stepConfigs.noConfigNeeded'),
                },
            ]
        },
        currentStepConfig() {
            return this.stepConfigs.find((c) => c.step === this.currentStep) || null
        },
        currentStepKey() {
            if (this.currentStep === 1) return 'integration'
            if (this.currentStep === 2) return this.selectedWidget?.id === 'activite' ? 'activityPage' : 'activitySelection'
            return 'integration'
        },
        iframeSrc() {
            if (!this.selectedWidget) return null

            if (this.selectedWidget.id === 'activite') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }

                if (this.selectedItem.isCombo) {
                    return `${this.url_front}/discover/combos/${this.selectedItem.label}?id=${this.selectedItem.originalId}`
                } else {
                    return `${this.url_front}/discover/reservation/${this.selectedItem.slug}`
                }
            }
            if (this.selectedWidget.id === 'evenements') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }
                return `${this.url_front}/discover/events/${this.selectedItem.slug}`
            }
            if (this.selectedWidget.id === 'formules') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }
                return `${this.url_front}/discover/combos/${this.selectedItem.label}?id=${this.selectedItem.originalId}`
            }
            if (this.selectedWidget.id === 'abonnements') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }
                return `${this.url_front}/discover/serie/${this.selectedItem.slug}`
            }
            if (this.selectedWidget.id === 'billets-non-dates') {
                return this.selectedWidget.url_front
            }
            if (this.selectedWidget.id === 'forfaits') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }
                return `${this.url_front}/passes/${this.selectedItem.slug}`
            }
            if (this.selectedWidget.id === 'devis') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }
                return `${this.url_front}/contact-us/${this.selectedItem.slug}`
            }
            if (this.selectedWidget.id === 'categorie') {
                if (!this.selectedItem) {
                    return this.selectedWidget.url_front
                }
                return `${this.url_front}/category/${this.selectedItem.slug}`
            }
            return this.selectedWidget.url_front
        },
        buttonText() {
            if (!this.selectedWidget) return ''
            if (this.selectedWidget.needsActivitySelection) {
                return this.currentStep === 1 ? this.$t('widgetIntegration.next') : this.$t('widgetIntegration.generateCode')
            }
            return this.$t('widgetIntegration.generateCode')
        },
        isBackButtonVisible() {
            return this.currentStep > 1 && this.selectedWidget && this.selectedWidget.needsActivitySelection
        },
        isActionButtonVisible() {
            return !!this.selectedWidget
        },
        selectedItem() {
            if (this.selectedWidget?.id === 'evenements') {
                if (!this.selectedEventId) return null
                return this.events.find((item) => item.originalId === this.selectedEventId) || null
            }
            if (this.selectedWidget?.id === 'formules') {
                if (!this.selectedFormuleCombineeId) return null
                return this.formulesCombinees.find((item) => item.originalId === this.selectedFormuleCombineeId) || null
            }
            if (this.selectedWidget?.id === 'abonnements') {
                if (!this.selectedSerieId) return null
                return this.series.find((item) => item.originalId === this.selectedSerieId) || null
            }
            if (this.selectedWidget?.id === 'billets-non-dates') {
                if (!this.selectedVoucherId) return null
                return this.vouchers.find((item) => item.originalId === this.selectedVoucherId) || null
            }
            if (this.selectedWidget?.id === 'forfaits') {
                if (!this.selectedForfaitId) return null
                return this.forfaits.find((item) => item.originalId === this.selectedForfaitId) || null
            }
            if (this.selectedWidget?.id === 'devis') {
                if (!this.selectedDevisId) return null
                return this.devis.find((item) => item.originalId === this.selectedDevisId) || null
            }
            if (this.selectedWidget?.id === 'categorie') {
                if (!this.selectedCategoryId) return null
                return this.categories.find((item) => item.originalId === this.selectedCategoryId) || null
            }
            if (!this.selectedActivityId) {
                return null
            }
            return this.activities.find((item) => item.originalId === this.selectedActivityId) || null
        },
        actionKeys() {
            const isCombo = this.selectedItem && this.selectedItem.isCombo
            return {
                modify: isCombo ? 'widgetIntegration.modifyCombo' : 'widgetIntegration.modifyActivity',
                create: isCombo ? 'widgetIntegration.createNewCombo' : 'widgetIntegration.createNewActivity',
            }
        },
        headerTitle() {
            if (this.selectedWidget?.id === 'restaurant') {
                return this.$t('widgetIntegration.headers.restaurant.title')
            }
            if (this.selectedWidget?.id === 'activite') {
                return this.$t('widgetIntegration.headers.activite.title')
            }
            if (this.selectedWidget?.id === 'bon-cadeau') {
                return this.$t('widgetIntegration.headers.bon-cadeau.title')
            }
            if (this.selectedWidget?.id === 'panier') {
                return this.$t('widgetIntegration.headers.panier.title')
            }
            if (this.selectedWidget?.id === 'evenements') {
                return this.$t('widgetIntegration.headers.evenements.title')
            }
            if (this.selectedWidget?.id === 'formules') {
                return this.$t('widgetIntegration.headers.formules.title')
            }
            if (this.selectedWidget?.id === 'abonnements') {
                return this.$t('widgetIntegration.headers.abonnements.title')
            }
            if (this.selectedWidget?.id === 'billets-non-dates') {
                return this.$t('widgetIntegration.headers.billets-non-dates.title')
            }
            if (this.selectedWidget?.id === 'devis') {
                return this.$t('widgetIntegration.headers.devis.title')
            }
            if (this.selectedWidget?.id === 'categorie') {
                return this.$t('widgetIntegration.headers.categorie.title')
            }
            if (this.selectedWidget?.id === 'forfaits') {
                return this.$t('widgetIntegration.headers.forfaits.title')
            }
            if (this.selectedWidget?.id === 'profile') {
                return this.$t('widgetIntegration.headers.profile.title')
            }
            return this.$t('widgetIntegration.headers.default.title')
        },
        headerDescription() {
            if (this.selectedWidget?.id === 'activite') {
                return this.$t('widgetIntegration.headers.activite.description')
            }
            if (this.selectedWidget?.id === 'restaurant') {
                return this.$t('widgetIntegration.headers.restaurant.description')
            }
            if (this.selectedWidget?.id === 'bon-cadeau') {
                return this.$t('widgetIntegration.headers.bon-cadeau.description')
            }
            if (this.selectedWidget?.id === 'panier') {
                return this.$t('widgetIntegration.headers.panier.description')
            }
            if (this.selectedWidget?.id === 'profile') {
                return this.$t('widgetIntegration.headers.profile.description')
            }
            if (this.selectedWidget?.id === 'evenements') {
                return this.$t('widgetIntegration.headers.evenements.description')
            }
            if (this.selectedWidget?.id === 'formules') {
                return this.$t('widgetIntegration.headers.formules.description')
            }
            if (this.selectedWidget?.id === 'abonnements') {
                return this.$t('widgetIntegration.headers.abonnements.description')
            }
            if (this.selectedWidget?.id === 'billets-non-dates') {
                return this.$t('widgetIntegration.headers.billets-non-dates.description')
            }
            if (this.selectedWidget?.id === 'devis') {
                return this.$t('widgetIntegration.headers.devis.description')
            }
            if (this.selectedWidget?.id === 'categorie') {
                return this.$t('widgetIntegration.headers.categorie.description')
            }
            if (this.selectedWidget?.id === 'forfaits') {
                return this.$t('widgetIntegration.headers.forfaits.description')
            }
            if (this.selectedWidget?.id === 'profile') {
                return this.$t('widgetIntegration.headers.profile.description')
            }
            return this.$t('widgetIntegration.headers.default.description')
        },
    },
    watch: {
        async selectedActivityId(newActivityId, oldActivityId) {
            if (
                newActivityId &&
                newActivityId !== oldActivityId &&
                this.selectedWidget &&
                this.selectedWidget.id === 'activite'
            ) {
                await this.checkActivityValidityAndUpdateIframe()
                if (this.activityIsValid) {
                    this.$nextTick(() => this.generateCode(false))
                }
            }
        },
        async selectedEventId(newId, oldId) {
            if (newId && newId !== oldId && this.selectedWidget && this.selectedWidget.id === 'evenements') {
                this.isLoadingIframe = true
                await this.$nextTick()
                this.generateCode(false)
            }
        },
        async selectedFormuleCombineeId(newId, oldId) {
            if (newId && newId !== oldId && this.selectedWidget && this.selectedWidget.id === 'formules') {
                this.isLoadingIframe = true
                await this.$nextTick()
                this.generateCode(false)
            }
        },
        async selectedSerieId(newId, oldId) {
            if (newId && newId !== oldId && this.selectedWidget && this.selectedWidget.id === 'abonnements') {
                this.isLoadingIframe = true
                await this.$nextTick()
                this.generateCode(false)
            }
        },
        async selectedVoucherId(newId, oldId) {
            if (newId && newId !== oldId && this.selectedWidget && this.selectedWidget.id === 'billets-non-dates') {
                this.isLoadingIframe = true
                await this.$nextTick()
                this.generateCode(false)
            }
        },
        async selectedForfaitId(newId, oldId) {
            if (newId && newId !== oldId && this.selectedWidget && this.selectedWidget.id === 'forfaits') {
                this.isLoadingIframe = true
                await this.$nextTick()
                this.generateCode(false)
            }
        },
        async selectedDevisId(newId, oldId) {
            if (newId && newId !== oldId && this.selectedWidget && this.selectedWidget.id === 'devis') {
                this.isLoadingIframe = true
                await this.$nextTick()
                this.generateCode(false)
            }
        },
        async selectedCategoryId(newId, oldId) {
            if (newId && newId !== oldId && this.selectedWidget && this.selectedWidget.id === 'categorie') {
                this.isLoadingIframe = true
                await this.fetchCategoryPrestations(newId)
                this.$nextTick(() => this.generateCode(false))
            }
        },
        iframeSrc(newUrl, oldUrl) {
            if (newUrl && newUrl !== oldUrl) {
                this.isLoadingIframe = true
            }
        },
        cookieConsentExp() {
            if (this.selectedWidget && this.generatedCodeSnippet) {
                this.$nextTick(() => this.generateCode(false))
            }
        },
    },
    created() {
        // Set language from establishment
        if (this.lang && this.$i18n) {
            this.$i18n.locale = this.lang
        }
    },
    mounted() {
        window.addEventListener('focus', this.handleWindowFocus)
        this.fetchBackgroundColor()
    },
    beforeDestroy() {
        window.removeEventListener('focus', this.handleWindowFocus)
    },
    methods: {
        selectWidget(widget) {
            this.selectedWidget = widget
            this.currentStep = widget.key
            this.selectedActivityId = null
            this.selectedFormuleCombineeId = null
            this.selectedEventId = null
            this.selectedSerieId = null
            this.selectedVoucherId = null
            this.selectedForfaitId = null
            this.selectedDevisId = null
            this.selectedCategoryId = null
            this.categoryPrestations = []
            this.activityIsValid = true
            this.activityError = null
            this.isLoadingIframe = false
            if (widget.id === 'activite') {
                this.fetchActivities()
            }
            if (widget.id === 'evenements') {
                this.fetchEvent()
            }
            if (widget.id === 'formules') {
                this.fetchFormulesCombinees()
            }
            if (widget.id === 'abonnements') {
                this.fetchSeries()
            }
            if (widget.id === 'devis') {
                this.fetchDevis()
            }
            if (widget.id === 'categorie') {
                this.fetchCategories()
            }
            if (widget.id === 'forfaits') {
                this.fetchForfaits()
            }
            if (!widget.needsActivitySelection) {
                this.$nextTick(() => this.generateCode(false))
            }
        },
        async fetchForfaits() {
            this.submitting = true
            try {
                const response = await axios.get('/api/v1/treatment/getOffres')
                const regularForfaits = response.data.data.map((forfait) => ({
                    label: forfait.name,
                    value: `forfait-${forfait.id}`,
                    originalId: forfait.id,
                    slug: forfait.id,
                    price: forfait.price,
                    prestations: forfait.prestations,
                }))
                this.forfaits = [...regularForfaits]

                // Set the first activity as selected by default if it exists
                if (this.forfaits.length > 0 && !this.selectedForfaitId) {
                    this.selectedForfaitId = this.forfaits[0].originalId
                }
                this.isLoadingIframe = false
            } catch (error) {
                console.error('Error fetching packages:', error)
                this.forfaits = []
            } finally {
                this.submitting = false
            }
        },
        async fetchDevis() {
            this.submitting = true
            try {
                const demandeDevisListResponse = await axios.get('/api/v1/etablissement/website')
                const blocDevis = demandeDevisListResponse.data.settings.bloc_devis

                this.devis = Object.entries(blocDevis)
                    .filter(([key]) => !isNaN(Number(key)))
                    .map(([key, item]) => {
                        const title =
                            item.title_first_demande_devis || item.title_second_demande_devis || item.title_third_demande_devis
                        const descrption =
                            item.desc_first_demande_devis || item.desc_second_demande_devis || item.desc_third_demande_devis

                        let type = 'evg'
                        if (item.title_first_demande_devis) type = 'company'
                        else if (item.title_second_demande_devis) type = 'anniversary'

                        return {
                            label: title,
                            value: `devis-${key}`,
                            originalId: key,
                            slug: type,
                            description: descrption,
                        }
                    })

                if (this.devis.length > 0 && !this.selectedDevisId) {
                    this.selectedDevisId = this.devis[0].originalId
                }
                this.isLoadingIframe = false
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_quotes_error'))
            } finally {
                this.submitting = false
            }
        },
        async fetchCategories() {
            this.submitting = true
            try {
                const categoriesResponse = await axios.get('/api/v1/tag/')

                this.categories = categoriesResponse.data.data
                    .filter((s) => s.is_visible !== false && s.hasContent !== false)
                    .map((cat) => ({
                        label: cat.name,
                        value: `cat-${cat.id}`,
                        originalId: cat.id,
                        slug: cat.slug,
                        activities: (cat.prestations || []).map((a) => ({
                            label: a.name,
                            slug: a.slug,
                            originalId: a.id,
                            status: a.status || (a.visible_online ? 'Publiée' : null),
                        })),
                    }))

                if (this.categories.length > 0 && !this.selectedCategoryId) {
                    this.selectedCategoryId = this.categories[0].originalId
                }
                this.isLoadingIframe = false
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_categories_error'))
            } finally {
                this.submitting = false
            }
        },
        async fetchCategoryPrestations(categoryValue) {
            try {
                const categoryId = String(categoryValue).replace('cat-', '')
                const response = await axios.get(`/api/v1/tag/${categoryId}/details`)

                const prestations = response.data.data.prestations || []
                const packages = response.data.data.packages || []

                const mappedPrestations = prestations.map((p) => ({
                    label: p.name,
                    value: `prest-${p.id}`,
                    originalId: p.id,
                    slug: p.slug,
                    status: p.visible_online ? 'Publiée' : null,
                    type: 'prestation', // Useful if you need to differentiate them later
                }))

                const mappedPackages = packages.map((pkg) => ({
                    label: pkg.name, // Adjust if the property name is different (e.g., pkg.title)
                    value: `pack-${pkg.id}`,
                    originalId: pkg.id,
                    slug: pkg.slug,
                    status: pkg.visible_online ? 'Publiée' : null,
                    type: 'package',
                }))

                // 4. Combine both into your component state
                this.categoryPrestations = [...mappedPrestations, ...mappedPackages]
            } catch (error) {
                this.categoryPrestations = []
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_services_error'))
            }
        },
        async fetchFormulesCombinees() {
            this.submitting = true
            try {
                const comboListResponse = await axios.get('/api/v1/activite/combo-list?get-all=1')
                const combos = comboListResponse.data.data.map((combo) => ({
                    label: combo.name,
                    value: `combo-${combo.id}`,
                    originalId: combo.id,
                    price: combo.price,
                    slug: `${combo.id}`,
                    // Combos fetched from combo-list are always published
                    status: combo.status || 'Publiée',
                    isCombo: true,
                }))
                this.formulesCombinees = [...combos]
                if (this.formulesCombinees.length > 0 && !this.selectedFormuleCombineeId) {
                    this.selectedFormuleCombineeId = this.formulesCombinees[0].originalId
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_combos_error'))
            } finally {
                this.submitting = false
            }
        },
        async fetchEvent() {
            this.submitting = true
            try {
                const now = new Date()

                // 2. Format it to 'YYYY-MM-DD HH:mm' (matching your exact format)
                const year = now.getFullYear()
                const month = String(now.getMonth() + 1).padStart(2, '0')
                const day = String(now.getDate()).padStart(2, '0')
                const hours = String(now.getHours()).padStart(2, '0')
                const minutes = String(now.getMinutes()).padStart(2, '0')

                const formattedTime = `${year}-${month}-${day} ${hours}:${minutes}`
                const response = await axios.get('/api/v1/activite/events', {
                    params: {
                        afterTime: formattedTime,
                    },
                })
                const regularEvents = response.data.events.map((event) => ({
                    label: event.name,
                    value: `event-${event.id}`,
                    originalId: event.id,
                    slug: event.slug,
                }))

                this.events = [...regularEvents]

                // Set the first activity as selected by default if it exists
                if (this.events.length > 0 && !this.selectedEventId) {
                    this.selectedEventId = this.events[0].originalId
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_events_error'))
            } finally {
                this.submitting = false
            }
        },
        async fetchVouchers() {
            this.submitting = true
            try {
                const response = await axios.get('/api/v1/activite/', {
                    params: {
                        isVoucher: 1,
                        pricesByTimes: true,
                    },
                })
                const list = response.data.data || []
                this.vouchers = list.map((voucher) => ({
                    label: voucher.name,
                    value: `voucher-${voucher.id}`,
                    originalId: voucher.id,
                    slug: voucher.slug,
                    price: voucher.price,
                    archiver: voucher.archiver,
                    visible_online: voucher.visible_online,
                    status: voucher.status || (voucher.visible_online ? 'Publiée' : null),
                }))

                if (this.vouchers.length > 0 && !this.selectedVoucherId) {
                    this.selectedVoucherId = this.vouchers[0].originalId
                }
                this.isLoadingIframe = false
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_vouchers_error'))
            } finally {
                this.submitting = false
            }
        },
        async fetchSeries() {
            this.submitting = true
            try {
                const response = await axios.get('/api/v1/serie/getSeries')
                const regularSeries = response.data.data.map((serie) => ({
                    label: serie.name,
                    value: `serie-${serie.id}`,
                    originalId: serie.id,
                    slug: serie.id,
                    price: serie.price,
                    archiver: serie.archiver,
                    subcategory: serie.subcategory,
                    visible_online: serie.visible_online,
                    status: serie.status || (serie.visible_online ? 'Publiée' : null),
                }))
                this.series = [...regularSeries]

                // Set the first activity as selected by default if it exists
                if (this.series.length > 0 && !this.selectedSerieId) {
                    this.selectedSerieId = this.series[0].originalId
                }
                this.isLoadingIframe = false
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_series_error'))
            } finally {
                this.submitting = false
            }
        },
        async fetchActivities() {
            this.submitting = true
            try {
                const response = await axios.get('/back-office/prestations/category/2')
                const regularActivities = response.data
                    .map((activity) => ({
                        label: activity.name,
                        value: `activity-${activity.id}`,
                        originalId: activity.id,
                        slug: activity.slug,
                        archiver: activity.archiver,
                        subcategory: activity.subcategory,
                        visible_online: activity.visible_online,
                        status: activity.status || (activity.visible_online ? 'Publiée' : null),
                        isCombo: false,
                    }))
                    .filter(
                        (activity) =>
                            !activity.subcategory ||
                            !['simpleTicket', 'simple-ticket', 'anniversaire', 'event', 'events', 'Events'].includes(
                                activity.subcategory.slug
                            )
                    )
                this.activities = [...regularActivities]

                // Set the first activity as selected by default if it exists
                if (this.activities.length > 0 && !this.selectedActivityId) {
                    this.selectedActivityId = this.activities[0].originalId
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.load_activities_error'))
            } finally {
                this.submitting = false
            }
        },
        handleActionButtonClick() {
            if (this.selectedWidget.needsActivitySelection) {
                if (this.currentStep === 1) {
                    this.currentStep = 2
                    this.fetchActivities()
                } else {
                    this.generateCode()
                }
            } else {
                this.generateCode()
            }
        },
        resetToInitialState() {
            this.selectedWidget = null
            this.currentStep = 1
            this.selectedActivityId = null
            this.selectedFormuleCombineeId = null
            this.selectedEventId = null
            this.selectedSerieId = null
            this.selectedVoucherId = null
            this.selectedForfaitId = null
            this.selectedDevisId = null
            this.selectedCategoryId = null
            this.activityError = null
            this.activityIsValid = true
            this.isLoadingIframe = false
            this.activities = []
            this.formulesCombinees = []
            this.events = []
            this.series = []
            this.vouchers = []
            this.forfaits = []
            this.devis = []
            this.categories = []
            this.categoryPrestations = []
            this.cookieConsentExp = false
            this.generatedCodeSnippet = ''
        },
        handleUpdateStep(step) {
            this.resetToInitialState()
        },
        handleBackClick() {
            this.resetToInitialState()
        },
        async generateCode(showModal = true) {
            if (!this.iframeSrc) {
                alert(this.$t('widgetIntegration.completeConfiguration'))
                return
            }

            this.isLoadingIframe = false

            // Check activity validity for 'activite' widget
            if (this.selectedWidget.id === 'activite') {
                this.submitting = true
                const validity = await this.checkActivityValidity()
                this.submitting = false
                if (!validity.isValid) {
                    alert(validity.error)
                    return
                }
            }

            if (this.selectedWidget.id === 'restaurant') {
                this.generatedCodeSnippet =
                    `<div data-playpro-widget="food"></div>\n<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'panier') {
                this.generatedCodeSnippet =
                    `<div data-playpro-widget="cart"></div>\n<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'activite') {
                const widgetType = this.cookieConsentExp ? 'reservation-page' : 'activities'
                const slugAttr =
                    this.cookieConsentExp && this.selectedItem?.slug
                        ? `data-slug="${this.escapeAttr(this.selectedItem.slug)}"`
                        : ''

                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType, slugAttr)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'evenements') {
                const widgetType = this.cookieConsentExp ? 'event' : 'events'
                const slugAttr =
                    this.cookieConsentExp && this.selectedItem?.slug
                        ? `data-slug="${this.escapeAttr(this.selectedItem.slug)}"`
                        : ''

                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType, slugAttr)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'formules') {
                const widgetType = this.cookieConsentExp ? 'combos' : 'combos'
                const slugAttr =
                    this.cookieConsentExp && this.selectedItem?.slug
                        ? `data-slug="${this.escapeAttr(this.selectedItem.slug)}"`
                        : ''

                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType, slugAttr)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'abonnements') {
                const widgetType = this.cookieConsentExp ? 'serie' : 'series'
                const item = this.selectedItem
                const slugAttr =
                    this.cookieConsentExp && item?.slug
                        ? `data-slug="${this.escapeAttr(item.slug)}"`
                        : this.cookieConsentExp && item?.originalId
                          ? `data-id="${this.escapeAttr(item.originalId)}"`
                          : ''

                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType, slugAttr)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'billets-non-dates') {
                this.generatedCodeSnippet =
                    `<div data-playpro-widget="reservation-page" data-slug="undated-ticket"></div>\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'devis') {
                const widgetType = this.cookieConsentExp ? 'request' : 'request-list'
                const item = this.selectedItem
                const slugAttr =
                    this.cookieConsentExp && item?.slug
                        ? `data-slug="${this.escapeAttr(item.slug)}"`
                        : this.cookieConsentExp && item?.originalId
                          ? `data-id="${this.escapeAttr(item.originalId)}"`
                          : ''

                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType, slugAttr)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'categorie') {
                const widgetType = 'tag'
                const slugAttr = this.selectedItem?.originalId
                    ? `data-slug="${this.escapeAttr(this.selectedItem.originalId)}"`
                    : ''

                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType, slugAttr)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'forfaits') {
                const widgetType = this.cookieConsentExp ? 'offer' : 'offers'
                const slugAttr =
                    this.cookieConsentExp && this.selectedItem?.originalId
                        ? `data-slug="${this.escapeAttr(this.selectedItem.originalId)}"`
                        : ''

                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType, slugAttr)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'bon-cadeau') {
                const widgetType = 'gift'
                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else if (this.selectedWidget.id === 'profile') {
                const widgetType = 'profile'
                this.generatedCodeSnippet =
                    `${this.buildWidgetDiv(widgetType)}\n` +
                    `<script src="${this.url_front}/widgets/playpro-widgets.min.js" defer></scr` +
                    `ipt>`
            } else {
                this.generatedCodeSnippet = `
    <iframe
      src="${this.iframeSrc}"
      style="width:100%; height:100vh; border:none;"
      loading="eager"
      scrolling="yes"
    ></iframe>
      `.trim()
            }

            if (showModal) {
                this.isModalVisible = true
            }
        },
        onIframeLoad() {
            this.isLoadingIframe = false
        },
        onCodeCopied(snippet) {
            this.generatedCodeSnippet = snippet
            this.isModalVisible = false
        },
        handleWindowFocus() {
            if (this.currentStep === 2) {
                this.fetchActivities()
            }
        },

        handleModifyClick(event) {
            if (!this.selectedItem) {
                event.preventDefault()
                return
            }
            // Let the horizontal link target="_blank" handle the opening
            // but we can add tracking or additional logic here if needed.
        },
        handleCreateClick() {
            // Always redirect to experience create page via direct link
        },
        async checkActivityValidity() {
            if (!this.selectedActivityId || !this.selectedWidget || this.selectedWidget.id !== 'activite') {
                return { isValid: true, error: null }
            }

            const activity = this.selectedItem

            if (!activity) {
                return { isValid: false, error: this.$t('widgetIntegration.activityNotFound') }
            }
            try {
                let apiUrl
                if (activity.isCombo === false) {
                    apiUrl = `${this.url_back}/api/v1/activite/${activity.slug}`
                } else {
                    apiUrl = `${this.url_back}/api/v1/activite/combo/${activity.originalId}`
                }
                const response = await axios.get(apiUrl)

                if (response.status === 200 && response.data) {
                    return { isValid: true, error: null }
                } else {
                    return { isValid: false, error: this.$t('widgetIntegration.activityNotAccessible') }
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('widget.verify_activity_error'))
                console.error('Error checking activity validity:', error)
                return { isValid: false, error: this.$t('widgetIntegration.activityError') }
            }
        },
        async checkActivityValidityAndUpdateIframe() {
            this.activityIsValid = false
            this.isLoadingIframe = true

            const validity = await this.checkActivityValidity()
            this.activityIsValid = validity.isValid
            this.activityError = validity.error

            this.isLoadingIframe = false
        },
        escapeAttr(value) {
            if (!value) return ''
            const str = String(value)
            if (/^[a-z0-9-]+$/i.test(str)) return str
            return encodeURIComponent(str)
        },
        buildWidgetDiv(widgetType, extraAttr = '') {
            const extra = extraAttr ? ` ${extraAttr.trim()}` : ''
            return `<div data-playpro-widget="${widgetType}"${extra}></div>`
        },
        async fetchBackgroundColor() {
            try {
                const response = await axios.get('/api/v1/etablissement/website')
                const settings = response.data.settings || {}
                if (settings.backgroundColor) {
                    this.backgroundColor = settings.backgroundColor
                }
            } catch (e) {
                // keep default white
            }
        },
    },
}
</script>

<style scoped>
body {
    font-family: 'Poppins', sans-serif;
}

.bg-light {
    background-color: #f7f8fc !important;
}

.highlight {
    background-color: #ffdd59;
    padding: 0 0.5rem;
    border-radius: 8px;
}

.widget-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border-width: 2px !important;
    margin-bottom: 1.5rem;
    border-radius: 12px !important;
}

.widget-card:hover {
    border-color: #7d2dbd80 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.widget-card.active-card {
    border-color: #7d2dbd !important;
    background-color: rgb(245 245 245 / 5%) !important;
    box-shadow: 0 4px 20px rgba(125, 45, 189, 0.1);
}

.widget-icon {
    padding-left: 1.5rem !important;
    padding-right: 1.5rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.widget-icon svg {
    width: 60px !important;
    height: 60px !important;
}

.btn-dark-blue {
    background-color: #192a56;
    color: white;
    font-weight: 600;
    padding: 0.7rem 3rem;
}

.btn-dark-blue:hover {
    background-color: #2c3e50;
    color: white;
}

.loading-overlay {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    background-color: rgba(255, 255, 255, 0.75) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10 !important;
}

.error-overlay {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    background-color: rgba(255, 255, 255, 0.9) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10 !important;
}

.activity-actions-box {
    border: 1px dotted #a4b0be;
    border-radius: 12px;
    padding: 1.25rem;
    background-color: #f8f9fa;
}

.action-link-wrapper a {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #192a56;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
}

.action-link-wrapper a span {
    text-decoration: underline;
}

.action-link-wrapper a:hover {
    color: #2c3e50;
}

.action-icon {
    color: #3e4254;
}

.disabled-link {
    color: #a4b0be !important;
    pointer-events: none;
    cursor: not-allowed;
}

.disabled-link span {
    text-decoration: none !important;
}

.header-title {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 28px;
    line-height: 20px;
    letter-spacing: 0%;
    margin-bottom: 0.5rem;
}

.header-description {
    font-family: 'Poppins', sans-serif;
    font-weight: 400;
    font-size: 17px;
    line-height: 20px;
    letter-spacing: 0%;
    color: #6c757d;
}
</style>
